import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { RegexService } from 'src/app/services/regex.service';
import { UserService } from 'src/app/services/user.service';
import Countries from 'src/assets/json/countries.json';
import { AddUserDialogComponent } from '../../users/add-user-dialog/add-user-dialog.component';
import { MessagingConversation } from 'src/app/models/messaging_conversation.model';
import { InputDialogComponent } from 'src/app/components/main/shared/input-dialog/input-dialog.component';
import { CompanyDetailSlackSectionComponent } from './company-detail-slack-section/company-detail-slack-section.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatPrefix, MatFormField, MatLabel, MatSuffix, MatHint } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { TitleCasePipe, DatePipe } from '@angular/common';
import { LocationsManagementComponent } from 'src/app/components/main/shared/locations/locations-management/locations-management.component';
import { LocationRelatableTypeEnum } from 'src/app/enums/location-relatable-type.enum';
import { CompanyDetailSectionEnum, CompanyDetailSectionEnumLabels } from 'src/app/enums/company-detail-section.enum';
import { Company } from 'src/app/models/company.model';
import { CompanyDetailHeaderAction } from './company-detail-header-action';

@Component({
  selector: 'app-company-detail',
  templateUrl: './company-detail.component.html',
  styleUrls: ['./company-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButton,
    RouterLink,
    MatIcon,
    MatPrefix,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSuffix,
    MatHint,
    MatSelect,
    MatOption,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TitleCasePipe,
    DatePipe,
    LocationsManagementComponent,
    CompanyDetailSlackSectionComponent,
  ],
})
export class CompanyDetailComponent implements OnInit {
  private readonly activated_route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly regex = inject(RegexService);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly user_service = inject(UserService);
  readonly permissions = inject(PermissionsService);

  protected readonly page_title = this.activated_route.snapshot.title;
  readonly company_relatable_type = LocationRelatableTypeEnum.Company;
  readonly company_detail_section_enum = CompanyDetailSectionEnum;
  readonly company_detail_section_enum_labels = CompanyDetailSectionEnumLabels;

  private readonly resolved_company = this.resolve_company();
  readonly company_id = this.resolved_company.company_id;
  readonly is_my_company_route = this.resolved_company.is_my_company_route;
  readonly back_companies_link = this.resolve_back_companies_link();
  readonly countries: { name: string; alpha_2: string; 'country-code': string }[] = Countries;

  readonly company_form: UntypedFormGroup;
  readonly company_loaded = signal(false);
  readonly slack_external_tenant_id = signal<string | null>(null);
  private readonly company_change_tick = signal(0);
  private readonly form_status_tick = signal(0);

  usersDataSource = new MatTableDataSource<User>([]);
  usersDisplayedColumns: string[] = ['name', 'email', 'role', 'actions'];
  messaging_conversations_data_source = new MatTableDataSource<MessagingConversation>([]);
  messaging_conversations_displayed_columns: string[] = ['provider', 'display_name', 'active', 'actions'];

  private readonly route_params = toSignal(this.activated_route.paramMap, {
    initialValue: this.activated_route.snapshot.paramMap,
  });

  readonly company_detail_sections: CompanyDetailSectionEnum[] = [
    CompanyDetailSectionEnum.General,
    CompanyDetailSectionEnum.Users,
    CompanyDetailSectionEnum.MessagingConversations,
    CompanyDetailSectionEnum.Communications,
    CompanyDetailSectionEnum.Locations,
  ];

  readonly can_edit_company = computed((): boolean => this.permissions.validateDepartment(['direction', 'finance']));
  readonly can_manage_slack = computed((): boolean => this.permissions.validateDepartment(['direction', 'rrhh']));

  readonly visible_company_detail_sections = computed((): CompanyDetailSectionEnum[] =>
    this.company_detail_sections.filter((section: CompanyDetailSectionEnum): boolean => this.section_is_visible(section))
  );

  private readonly requested_section = computed((): CompanyDetailSectionEnum | null => {
    const section = this.route_params().get('section');

    return this.is_company_detail_section(section) ? section : null;
  });

  readonly active_section = computed((): CompanyDetailSectionEnum => {
    this.company_change_tick();
    const requested_section = this.requested_section();

    return requested_section != null && this.section_is_visible(requested_section) ? requested_section : CompanyDetailSectionEnum.General;
  });

  readonly active_section_label = computed((): string => this.company_detail_section_enum_labels[this.active_section()]);

  readonly header_actions = computed((): CompanyDetailHeaderAction[] => {
    this.form_status_tick();
    this.company_change_tick();

    switch (this.active_section()) {
      case CompanyDetailSectionEnum.General:
        if (!this.can_edit_company()) {
          return [];
        }

        return [
          {
            label: 'Guardar',
            icon: 'save',
            color: 'accent',
            disabled: !this.company_form.valid,
            on_click: (): void => this.save(),
          },
        ];
      case CompanyDetailSectionEnum.Users:
        if (!this.can_edit_company()) {
          return [];
        }

        return [
          {
            label: 'Añadir usuario',
            icon: 'person_add',
            color: 'accent',
            on_click: (): void => this.addUser(),
          },
        ];
      case CompanyDetailSectionEnum.MessagingConversations:
        if (!this.can_manage_slack()) {
          return [];
        }

        return [
          {
            label: 'Añadir Teams (webhook)',
            icon: 'add',
            color: 'accent',
            on_click: (): void => this.addTeamsConversation(),
          },
        ];
      case CompanyDetailSectionEnum.Communications:
        return [];
      case CompanyDetailSectionEnum.Locations:
        return [];
    }
  });

  constructor() {
    const regex_patterns = this.regex.getRegex();

    this.company_form = this.fb.group({
      email: ['', Validators.compose([Validators.required, Validators.email])],
      phone: ['', Validators.compose([Validators.required, Validators.pattern(regex_patterns['phone'])])],
      fact_name: ['', Validators.compose([Validators.required])],
      fact_address: ['', Validators.compose([Validators.required])],
      fact_city: ['', Validators.compose([Validators.required])],
      fact_zipcode: ['', Validators.compose([Validators.required, Validators.minLength(5), Validators.maxLength(5)])],
      fact_county: ['', Validators.compose([Validators.required])],
      fact_country: ['', Validators.compose([Validators.required])],
      fact_country_alpha2: ['', Validators.compose([Validators.required])],
      vat: ['', Validators.compose([Validators.required])],
    });

    if (!this.can_edit_company()) {
      this.company_form.disable({ emitEvent: false });
    }

    this.company_form.controls['fact_country'].valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((data: string) => {
      for (const c of this.countries) {
        if (c.name == data) {
          this.company_form.patchValue({ fact_country_alpha2: c.alpha_2 });
          break;
        }
      }
    });

    this.company_form.statusChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((): void => {
      this.form_status_tick.update((tick: number): number => tick + 1);
    });

    this.activated_route.paramMap.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe((): void => this.sync_section_route());
  }

  ngOnInit(): void {
    this.fetchCompany();
  }

  select_section(section: CompanyDetailSectionEnum): void {
    void this.router.navigate(['../', section], {
      relativeTo: this.activated_route,
    });
  }

  save(): void {
    if (!this.can_edit_company()) {
      return;
    }

    this.api.updateCompany(this.company_id, this.company_form.getRawValue()).subscribe();
  }

  addUser(): void {
    const dialogRef = this.dialog.open(AddUserDialogComponent, {
      width: '750px',
      data: {
        company_id: this.company_id,
        lock_client_fields: true,
      },
    });

    dialogRef.afterClosed().subscribe((user: User) => {
      if (user != null) {
        this.fetchCompany();
      }
    });
  }

  addTeamsConversation(): void {
    const name_dialog = this.dialog.open(InputDialogComponent, {
      data: {
        title: 'Nueva conversación Teams',
        description: 'Nombre del canal',
        label: 'Nombre',
        close_button_text: 'Siguiente',
      },
      width: '480px',
    });

    name_dialog.afterClosed().subscribe((name_result: { value: string } | undefined) => {
      if (!name_result?.value) {
        return;
      }

      const webhook_dialog = this.dialog.open(InputDialogComponent, {
        data: {
          title: 'Webhook de Teams',
          description: 'Pega la Incoming Webhook URL del canal',
          label: 'Webhook URL',
          close_button_text: 'Crear',
        },
        width: '560px',
      });

      webhook_dialog.afterClosed().subscribe((webhook_result: { value: string } | undefined) => {
        if (!webhook_result?.value) {
          return;
        }

        this.api
          .createMessagingConversation({
            company_id: Number(this.company_id),
            provider: 'teams',
            display_name: name_result.value,
            teams_webhook_url: webhook_result.value,
          })
          .subscribe(() => this.fetchCompanyConversations());
      });
    });
  }

  deleteMessagingConversation(conversation: MessagingConversation): void {
    if (confirm('¿Eliminar esta conversación?')) {
      this.api.deleteMessagingConversation(conversation.id).subscribe(() => this.fetchCompanyConversations());
    }
  }

  on_tenant_saved(value: string | null): void {
    this.slack_external_tenant_id.set(value);
  }

  private fetchCompany(): void {
    this.api.getCompany(this.company_id, { with: 'users' }).subscribe((data: Company) => {
      this.company_form.patchValue(data);
      this.slack_external_tenant_id.set(data.slack_external_tenant_id ?? null);
      this.usersDataSource.data = data.users ?? [];
      this.company_loaded.set(true);
      this.company_change_tick.update((tick: number): number => tick + 1);
      this.sync_section_route();
      if (this.section_is_visible(CompanyDetailSectionEnum.MessagingConversations)) {
        this.fetchCompanyConversations();
      }
      this.change_detector_ref.markForCheck();
    });
  }

  private fetchCompanyConversations(): void {
    this.api.getMessagingConversations({ company_id: this.company_id, per_page: 200 }).subscribe(response => {
      this.messaging_conversations_data_source.data = response.data ?? [];
      this.change_detector_ref.markForCheck();
    });
  }

  private section_is_visible(section: CompanyDetailSectionEnum): boolean {
    switch (section) {
      case CompanyDetailSectionEnum.General:
      case CompanyDetailSectionEnum.Users:
        return true;
      case CompanyDetailSectionEnum.Communications:
        return this.can_manage_slack() || this.is_my_company_route;
      case CompanyDetailSectionEnum.MessagingConversations:
      case CompanyDetailSectionEnum.Locations:
        return this.permissions.isWorker();
      default:
        return false;
    }
  }

  private sync_section_route(): void {
    if (!this.company_loaded()) {
      return;
    }

    const active_section = this.active_section();

    if (this.route_params().get('section') === active_section) {
      return;
    }

    void this.router.navigate(['../', active_section], {
      relativeTo: this.activated_route,
      replaceUrl: true,
    });
  }

  private is_company_detail_section(section: string | null): section is CompanyDetailSectionEnum {
    return (
      section != null &&
      this.company_detail_sections.some((company_detail_section: CompanyDetailSectionEnum): boolean => company_detail_section === section)
    );
  }

  private resolve_company(): { company_id: string; is_my_company_route: boolean } {
    let route: typeof this.activated_route.snapshot | null = this.activated_route.snapshot;

    while (route != null) {
      const id = route.paramMap.get('id');
      if (id != null && id !== '') {
        return { company_id: id, is_my_company_route: false };
      }
      route = route.parent;
    }

    const company_id = this.user_service.getCurrentUser()?.company_id;
    if (company_id != null) {
      return { company_id: String(company_id), is_my_company_route: true };
    }

    throw new Error('Company detail route is missing :id and the current user has no company_id');
  }

  private resolve_back_companies_link(): string[] | null {
    if (this.is_my_company_route) {
      return null;
    }

    const url = this.router.url;
    if (url.includes('/partners/direction/companies/')) {
      return ['/partners', 'direction', 'companies'];
    }

    return ['/employees', 'direction', 'companies'];
  }
}
